import { gql } from '@apollo/client';

/**
 * 揽收进度页「揽收失败预约单」抽屉查询。
 * 网关 getPickupFailedAppointments → pickup-service PickupProblemAppointmentQueryDubboService。
 */
export const GET_PICKUP_FAILED_APPOINTMENTS = gql`
  query GetPickupFailedAppointments($filter: PickupProblemAppointmentFilterInput!) {
    getPickupFailedAppointments(filter: $filter) {
      content {
        appointmentId
        appointmentNumber
        taskNumber
        pickupSiteId
        pickupSiteName
        address
        driverId
        driverName
        driverPhone
        arrivedAt
      }
      page
      size
      totalElements
      totalPages
      hasNext
      hasPrevious
    }
  }
`;

/**
 * 揽收进度页「虚假上门预约单」抽屉查询（POP 判疑似虚假；含 finished 状态列）。
 * 与失败抽屉共用返回结构，仅 query 名与数据源不同。
 */
export const GET_SUSPECTED_FAKE_APPOINTMENTS = gql`
  query GetSuspectedFakeAppointments($filter: PickupProblemAppointmentFilterInput!) {
    getSuspectedFakeAppointments(filter: $filter) {
      content {
        appointmentId
        appointmentNumber
        taskNumber
        pickupSiteId
        pickupSiteName
        address
        driverId
        driverName
        driverPhone
        arrivedAt
        finished
      }
      page
      size
      totalElements
      totalPages
      hasNext
      hasPrevious
    }
  }
`;

/**
 * 揽收进度页「未上网预约单」抽屉查询。
 * 未上网 = 揽收成功、司机扫描的运单无一发生 ASCAN；揽收失败不计入。
 * 判定结果由入库计数消息物化到 pickup_order_scan_info.arrival_scan_at，查询侧纯 SQL。
 */
export const GET_PICKUP_NOT_ONLINE_APPOINTMENTS = gql`
  query GetPickupNotOnlineAppointments($filter: PickupProblemAppointmentFilterInput!) {
    getPickupNotOnlineAppointments(filter: $filter) {
      content {
        appointmentId
        appointmentNumber
        appointmentStatus
        taskNumber
        pickupSiteId
        pickupSiteName
        address
        driverId
        driverName
        driverPhone
      }
      page
      size
      totalElements
      totalPages
      hasNext
      hasPrevious
    }
  }
`;

/**
 * 揽收进度页「无法扫描上报预约单」抽屉查询。
 * 数据源为司机点「无法上报」时填写的 unscannable_reason；与「未上网」互斥（司机端二选一）。
 */
export const GET_PICKUP_UNSCANNABLE_APPOINTMENTS = gql`
  query GetPickupUnscannableAppointments($filter: PickupProblemAppointmentFilterInput!) {
    getPickupUnscannableAppointments(filter: $filter) {
      content {
        appointmentId
        appointmentNumber
        unscannableReason
        taskNumber
        pickupSiteId
        pickupSiteName
        address
        driverId
        driverName
        driverPhone
      }
      page
      size
      totalElements
      totalPages
      hasNext
      hasPrevious
    }
  }
`;

/**
 * 「未上网预约单」汇总：顶部卡片数字 + 主列表每任务聚合，一次查询喂两处。
 * total 不可用 countByTask 求和代替——未挂揽收任务的预约单不在 countByTask 里。
 */
export const GET_PICKUP_NOT_ONLINE_SUMMARY = gql`
  query GetPickupNotOnlineSummary($hubId: Long!, $pickupDate: String!) {
    getPickupNotOnlineSummary(hubId: $hubId, pickupDate: $pickupDate) {
      total
      countByTask {
        taskNumber
        count
      }
    }
  }
`;

export interface GetSuspectedFakeAppointmentsResponse {
  getSuspectedFakeAppointments: PickupProblemAppointmentPage;
}

export interface GetPickupNotOnlineAppointmentsResponse {
  getPickupNotOnlineAppointments: PickupProblemAppointmentPage;
}

export interface GetPickupUnscannableAppointmentsResponse {
  getPickupUnscannableAppointments: PickupProblemAppointmentPage;
}

export interface PickupNotOnlineTaskCount {
  taskNumber: string;
  count: number;
}

export interface GetPickupNotOnlineSummaryResponse {
  getPickupNotOnlineSummary: {
    total: number;
    countByTask: PickupNotOnlineTaskCount[];
  } | null;
}

export interface PickupProblemAppointmentItem {
  appointmentId: number;
  appointmentNumber?: string;
  taskNumber?: string;
  pickupSiteId?: number;
  pickupSiteName?: string;
  address?: string;
  driverId?: number;
  driverName?: string;
  driverPhone?: string;
  /** 上门时间 ISO-8601（actual_pickup_time） */
  arrivedAt?: string;
  /** 「虚假上门」状态：0-待处理，1-已完结；失败抽屉为 null */
  finished?: number;
  /** 预约单状态；「未上网」抽屉状态列取此字段，其余抽屉为 null */
  appointmentStatus?: string;
  /** 司机填写的无法扫描原因；「无法扫描上报」抽屉取此字段，其余抽屉为 null */
  unscannableReason?: string;
}

export interface PickupProblemAppointmentPage {
  content: PickupProblemAppointmentItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface GetPickupFailedAppointmentsResponse {
  getPickupFailedAppointments: PickupProblemAppointmentPage;
}

export interface PickupProblemAppointmentFilterInput {
  hubId: number;
  pickupDate: string;
  appointmentNumbers?: string[];
  driverKeyword?: string;
  contactKeyword?: string;
  pickupSiteIds?: number[];
  pageNum?: number;
  pageSize?: number;
}
